import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { useAuthStore } from "presentation/stores/use-auth-store";
import {
  useVacationPlanByFilter,
  useCreateVacationPlanMutation,
  useDeleteVacationPlanMutation,
  useUpdateVacationPlanMutation,
} from "presentation/hooks/use-query-vacation";
import { Vacation } from "domain/entities/vacations";
import { Tooltip } from "primereact/tooltip";
import Spinner from "presentation/components/Shared/Spinner";
import VacationBalance from "./VacationBalance";
import VacationSchedulerForm from "./VacationSchedulerForm";
import VacationList from "./VacationList";
import { VacationInputFilter } from "domain/models/vacation-input-filter";

const ScheduleVacation: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { userEmployee } = useAuthStore();
  const employeeId = userEmployee?.id;
  const [vacationDaysLastYear, setVacationDaysLastYear] = useState<number>(0);
  const [vacationDaysCurrentYear, setVacationDaysCurrentYear] =
    useState<number>(0);

  const [endDateLimit, setEndDateLimit] = useState<Date | undefined>(undefined);

  const filter: VacationInputFilter = {
    employeeId: employeeId ?? "",
    limit: 100
  };

  const {
    data: vacationData,
    isLoading,
    isRefetching,
  } = useVacationPlanByFilter(filter);

  useEffect(() => {
    const vacationDaysCurrentYear =
      vacationData?.data?.[0]?.employee?.vacationDaysCurrentYear ??
      userEmployee?.vacationDaysCurrentYear ??
      0;

    const vacationDaysLastYear =
      vacationData?.data?.[0]?.employee?.vacationDaysLastYear ??
      userEmployee?.vacationDaysLastYear ??
      0;

    setVacationDaysCurrentYear(vacationDaysCurrentYear);
    setVacationDaysLastYear(vacationDaysLastYear);
  }, [vacationData, userEmployee]);

  const { mutate: createVacation } = useCreateVacationPlanMutation();
  const { mutate: deleteVacation } = useDeleteVacationPlanMutation();
  const { mutate: updateVacation } = useUpdateVacationPlanMutation();

  const confirmAction = (message: string, acceptCallback: () => void) => {
    confirmDialog({
      message,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };

  const handleSchedule = (startDate: Date, endDate: Date) => {
    if (!employeeId) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Funcionário não identificado.",
        life: 3000,
      });
      return;
    }

    const payload: Vacation = {
      startAt: startDate,
      endAt: endDate,
      employee: { id: employeeId },
    };

    createVacation(payload, {
      onSuccess: () => {
        toast.current?.show({
          severity: "success",
          summary: "Agendamento",
          detail: "Férias agendadas com sucesso!",
          life: 2000,
        });
      },
      onError: (error: any) => {
        const regexVacationExists =
          /^Employee with id [0-9a-fA-F-]{36} has already VacationPlan\(s\) for some days requested\.$/;
        const regexNotEnoughDays =
          /^Employee with id [0-9a-fA-F-]{36} has not enough vacation days available\.$/;
        const regexInvalidDate = 
          /^VacationPlan for employee with id [0-9a-fA-F-]{36} has not valid days\.$/;

        const errorMessage = error?.response?.data?.metadata?.errors?.[0] || "";

        const msg = regexVacationExists.test(errorMessage)
          ? "Já existe um agendamento de férias para o período selecionado. Por favor, escolha outro período ou cancele o agendamento existente."
          : regexNotEnoughDays.test(errorMessage)
            ? "Não existem dias de férias suficientes disponíveis."
            : regexInvalidDate.test(errorMessage)
              ? "Não é possível agendar férias em feriados ou fins de semana."
              : "Erro ao agendar férias.";
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: msg,
          life: 5000,
        });
      },
    });
  };

  const handleCancel = (vacationPlan: Vacation) => {
    const performDelete = () => {

      if (vacationPlan.status === "A Aprovar") {
        deleteVacation(vacationPlan.id ?? "", {
          onSuccess: () => {
            toast.current?.show({
              severity: "success",
              summary: "Cancelado",
              detail: "Férias canceladas com sucesso!",
              life: 3000,
            });
          },
          onError: () => {
            toast.current?.show({
              severity: "error",
              summary: "Erro",
              detail: "Erro ao cancelar as férias.",
              life: 3000,
            });
          },
        });
      }
      else if (vacationPlan.status === "Aprovado") {
        vacationPlan.status = "Cancelamento Pendente";
        vacationPlan.rejectionReason = undefined;
        updateVacation(vacationPlan, {
          onSuccess: () => {
            toast.current?.show({
              severity: "success",
              summary: "Cancelado",
              detail: "Pedido de cancelamento das férias efetuado com sucesso!",
              life: 3000,
            });
          },
          onError: () => {
            toast.current?.show({
              severity: "error",
              summary: "Erro",
              detail: "Erro ao cancelar as férias.",
              life: 3000,
            });
          },
        });
      }
      else {
        toast.current?.show({
          severity: "info",
          summary: "Informação",
          detail: `Não é possível cancelar férias já no estado ${vacationPlan.status}`,
          life: 3000,
        });
      }
      
    };

    confirmAction("Deseja cancelar estas férias?", performDelete);
  };

  useEffect(() => {
    if (!endDateLimit) {
      const currentDate = new Date();
      const endYear = currentDate.getFullYear() + 1;

      const endDateLimit = new Date(endYear, 11, 31);
      setEndDateLimit(endDateLimit);
    }
  }, []);

  const onConfirmSchedule = (startDate: Date, endDate: Date) => {
    confirmAction("Deseja agendar essas férias?", () =>
      handleSchedule(startDate, endDate)
    );
  };

  if (isLoading || isRefetching) return <Spinner />;

  return (
    <div className="timesheet-grid">
      <VacationBalance
        lastYearDays={vacationDaysLastYear}
        currentYearDays={vacationDaysCurrentYear}
      />

      <VacationSchedulerForm
        onSchedule={onConfirmSchedule}
        endDateLimit={endDateLimit}
      />

      <VacationList
        vacations={vacationData?.data || []}
        isLoading={isLoading}
        onCancel={handleCancel}
      />

      <Toast ref={toast} position="bottom-right" />
      <Tooltip target="[data-pr-tooltip]" />
      <ConfirmDialog />
    </div>
  );
};

export default ScheduleVacation;
